import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  boardInviteAcceptResultSchema,
  boardInviteLinkOrNullSchema,
  boardInviteLinkSchema,
  createBoardInviteLinkBodySchema,
  workspaceJoinRequestListSchema,
  workspaceJoinRequestSchema,
  type ApiError,
  type BoardInviteAcceptResult,
  type BoardInviteLink,
  type GrantableBoardRole,
  type WorkspaceJoinRequest,
} from '@molt/shared';
import { del, get, post } from './api';
import { queryKeys } from './query-keys';

export function getBoardInviteLink(boardId: string): Promise<BoardInviteLink | null> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/invite-link`,
    boardInviteLinkOrNullSchema,
  );
}

export function createBoardInviteLink(
  boardId: string,
  variables?: { role?: GrantableBoardRole | undefined },
): Promise<BoardInviteLink> {
  const role = variables?.role;
  return role === undefined
    ? post(`/boards/${encodeURIComponent(boardId)}/invite-link`, boardInviteLinkSchema)
    : post(
        `/boards/${encodeURIComponent(boardId)}/invite-link`,
        boardInviteLinkSchema,
        createBoardInviteLinkBodySchema.parse({ role }),
      );
}

export function revokeBoardInviteLink(boardId: string): Promise<BoardInviteLink | null> {
  return del(
    `/boards/${encodeURIComponent(boardId)}/invite-link`,
    boardInviteLinkOrNullSchema,
  );
}

export function acceptBoardInvite(token: string): Promise<BoardInviteAcceptResult> {
  return post(
    `/board-invites/${encodeURIComponent(token)}/accept`,
    boardInviteAcceptResultSchema,
  );
}

export function getWorkspaceJoinRequests(
  workspaceId: string,
): Promise<WorkspaceJoinRequest[]> {
  return get(
    `/workspaces/${encodeURIComponent(workspaceId)}/join-requests`,
    workspaceJoinRequestListSchema,
  );
}

export function approveWorkspaceJoinRequest(
  workspaceId: string,
  requestId: string,
): Promise<WorkspaceJoinRequest> {
  return post(
    `/workspaces/${encodeURIComponent(workspaceId)}/join-requests/${encodeURIComponent(
      requestId,
    )}/approve`,
    workspaceJoinRequestSchema,
  );
}

export function declineWorkspaceJoinRequest(
  workspaceId: string,
  requestId: string,
): Promise<WorkspaceJoinRequest> {
  return post(
    `/workspaces/${encodeURIComponent(workspaceId)}/join-requests/${encodeURIComponent(
      requestId,
    )}/decline`,
    workspaceJoinRequestSchema,
  );
}

export function boardInviteLinkQueryOptions(
  boardId: string,
): UseQueryOptions<BoardInviteLink | null, ApiError> {
  return {
    queryKey: queryKeys.boards.inviteLink(boardId),
    queryFn: () => getBoardInviteLink(boardId),
  };
}

export function workspaceJoinRequestsQueryOptions(
  workspaceId: string,
): UseQueryOptions<WorkspaceJoinRequest[], ApiError> {
  return {
    queryKey: queryKeys.workspaces.joinRequests(workspaceId),
    queryFn: () => getWorkspaceJoinRequests(workspaceId),
  };
}

export function useBoardInviteLink(
  boardId: string,
  enabled: boolean,
): UseQueryResult<BoardInviteLink | null, ApiError> {
  return useQuery({ ...boardInviteLinkQueryOptions(boardId), enabled });
}

export function useCreateBoardInviteLink(
  boardId: string,
): UseMutationResult<
  BoardInviteLink,
  ApiError,
  { role?: GrantableBoardRole | undefined } | void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => createBoardInviteLink(boardId, variables ?? undefined),
    onSuccess: (link) => {
      queryClient.setQueryData(queryKeys.boards.inviteLink(boardId), link);
    },
  });
}

export function useRevokeBoardInviteLink(
  boardId: string,
): UseMutationResult<BoardInviteLink | null, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => revokeBoardInviteLink(boardId),
    onSuccess: (link) => {
      queryClient.setQueryData(queryKeys.boards.inviteLink(boardId), link);
    },
  });
}

export function useAcceptBoardInvite(
  token: string,
): UseMutationResult<BoardInviteAcceptResult, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => acceptBoardInvite(token),
    onSuccess: (result) => {
      invalidateAfterInviteAccept(queryClient, result);
    },
  });
}

export function useWorkspaceJoinRequests(
  workspaceId: string,
  enabled: boolean,
): UseQueryResult<WorkspaceJoinRequest[], ApiError> {
  return useQuery({ ...workspaceJoinRequestsQueryOptions(workspaceId), enabled });
}

export function useApproveWorkspaceJoinRequest(
  workspaceId: string,
): UseMutationResult<WorkspaceJoinRequest, ApiError, { requestId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId }) => approveWorkspaceJoinRequest(workspaceId, requestId),
    onSuccess: (request) => {
      updateJoinRequestCache(queryClient, workspaceId, request);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workspaces.members(workspaceId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workspaces.boards(workspaceId),
      });
    },
  });
}

export function useDeclineWorkspaceJoinRequest(
  workspaceId: string,
): UseMutationResult<WorkspaceJoinRequest, ApiError, { requestId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId }) => declineWorkspaceJoinRequest(workspaceId, requestId),
    onSuccess: (request) => {
      updateJoinRequestCache(queryClient, workspaceId, request);
    },
  });
}

export function replaceJoinRequest(
  previous: WorkspaceJoinRequest[] | undefined,
  next: WorkspaceJoinRequest,
): WorkspaceJoinRequest[] | undefined {
  if (previous === undefined) return previous;
  return previous.map((request) => (request.id === next.id ? next : request));
}

export function inviteUrlFromToken(origin: string, token: string): string {
  return `${origin.replace(/\/$/, '')}/board-invites/${encodeURIComponent(token)}`;
}

function updateJoinRequestCache(
  queryClient: QueryClient,
  workspaceId: string,
  request: WorkspaceJoinRequest,
): void {
  queryClient.setQueryData<WorkspaceJoinRequest[]>(
    queryKeys.workspaces.joinRequests(workspaceId),
    (previous) => replaceJoinRequest(previous, request),
  );
}

function invalidateAfterInviteAccept(
  queryClient: QueryClient,
  result: BoardInviteAcceptResult,
): void {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.boards.detail(result.boardId),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.boards.members(result.boardId),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.boards.contents(result.boardId),
  });
}
